import { api } from '@/lib/axios';

export interface LoginRequest { username: string; password: string; }
export interface LoginResponse {
  token?: string;
  userName: string;
  userKey: number;
  guidId: string;
  expiredTime?: string;
  session?: string;
  resetReason?: string;
  mfaRequired?: boolean;
  mfaToken?: string;
  mfaMethod?: 'email' | 'sms' | 'totp' | 'passkey';
  /** All enrolled methods — present when channel=picker, absent for single-method users. */
  mfaMethods?: string[];
  mfaChannel?: string | null;
  mfaDestination?: string | null;
  mfaSetupRequired?: boolean;
  setupToken?: string;
  requiresTermsAcceptance?: boolean;
  passwordResetRequired?: boolean;
  passwordResetReason?: 'forced' | 'weakPassword';
  csrfToken?: string;
}

export interface MfaVerifyRequest { mfaToken: string; code: string; }
export interface MfaVerifyResponse {
  userName: string;
  userKey: number;
  guidId: string;
  session?: string;
  passwordResetRequired?: boolean;
  passwordResetReason?: 'forced' | 'weakPassword';
  requiresTermsAcceptance?: boolean;
  /**
   * Scoped token (purpose="mfa_setup") returned alongside requiresTermsAcceptance when MFA
   * succeeded but pending terms block the session. Use it to call /Legal/PendingTerms and
   * /Legal/AcceptTerms, then POST /Authentication/CompleteAfterTerms to receive the session.
   */
  setupToken?: string;
  recoveryCodeUsed?: boolean;
  resetReason?: string;
  csrfToken?: string;
}

export interface SelectMfaMethodRequest { MfaToken: string; Method: string; }
export interface SelectMfaMethodResponse { mfaToken: string; channel: string; maskedDestination: string; }

export interface PasskeyCredentialInfo {
  credentialKey: number;
  deviceName: string;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface MfaMultiMethodSettings {
  smsEnrolled: boolean;
  emailEnrolled: boolean;
  totpEnrolled: boolean;
  maskedSmsNumber: string | null;
  maskedEmailAddress: string | null;
  enabledMethods: string[];
  recoveryCodeGenerationDate: string | null;
  hasExistingRecoveryCodes: boolean;
  passkeyEnrolled: boolean;
  passkeyCredentials: PasskeyCredentialInfo[] | null;
}

export interface EnableMfaMethodRequest { Method: string; Destination?: string | null; VerificationCode?: string | null; }
export interface DisableMfaMethodRequest { Method: string; }

export interface PasskeyRegistrationOptions { optionsJson: string; setupToken: string; }
export interface PasskeyRegistrationRequest { SetupToken: string; AttestationJson: string; DeviceName: string; }
export interface PasskeyRegistrationResult { success: boolean; errorCode?: string | null; }
export interface PasskeyAssertionOptions { optionsJson: string; }

export interface TotpSetupResponse { uri: string; qrCodeBase64: string; manualEntryKey: string; setupToken: string; }
export interface TotpConfirmResponse { success: boolean; errorCode?: string | null; }
export interface RecoveryCodesResponse { codes: string[]; recoveryCodeUsed?: boolean; }

export interface UpdatePasswordRequest { userKey: string; currentPassword: string; newPassword: string; confirmPassword: string; }
export interface UpdatePasswordResponse { success: boolean; messageCode?: string; passwordLastChanged?: string | null; }

export interface PasswordInfoResponse { userName: string; passwordLastChanged: string | null; simplePasswordFlag?: boolean; }

export interface GuestSessionResponse {
  userName: string;
  userKey: number;
  session?: string;
  patientKey?: number;
  csrfToken?: string;
}

export interface SessionTimingResponse {
  userName: string;
  userKey: number;
  session: string;
  csrfToken?: string;
  idleTimeoutMinutes: number;
  idleExpiresAt: string;
  absoluteExpiresAt: string;
}

export const authService = {
  login: (req: LoginRequest) =>
    api.post<LoginResponse>('/Authentication/Login', req).then(r => r.data),

  logout: () =>
    api.post('/Authentication/Logout').then(() => undefined),

  recordActivity: () =>
    api.post<SessionTimingResponse>('/Authentication/Activity').then(r => r.data),

  verifyMfa: (req: MfaVerifyRequest) =>
    api.post<MfaVerifyResponse>('/Authentication/MFA/Verify', req).then(r => r.data),

  selectMfaMethod: (req: SelectMfaMethodRequest) =>
    api.post<SelectMfaMethodResponse>('/Authentication/MFA/SelectMethod', req).then(r => r.data),

  getMfaSettings: () =>
    api.get<MfaMultiMethodSettings>('/Authentication/MFA/Settings').then(r => r.data),

  enableMfaMethod: (req: EnableMfaMethodRequest, setupToken?: string) =>
    api.post<MfaMultiMethodSettings>(
      '/Authentication/MFA/Settings/EnableMethod',
      req,
      setupToken ? { headers: { Authorization: `Bearer ${setupToken}` } } : undefined,
    ).then(r => r.data),

  disableMfaMethod: (req: DisableMfaMethodRequest) =>
    api.post<MfaMultiMethodSettings>('/Authentication/MFA/Settings/DisableMethod', req).then(r => r.data),

  sendMfaCode: (mfaToken: string) =>
    api.post<{ mfaToken: string | null }>('/Authentication/MFA/SendCode', { MfaToken: mfaToken }).then(r => r.data),

  sendMfaCodeForSettings: (destination: string, setupToken?: string) =>
    api.post<void>(
      '/Authentication/MFA/Settings/SendCode',
      { Destination: destination },
      setupToken ? { headers: { Authorization: `Bearer ${setupToken}` } } : undefined,
    ).then(() => undefined),

  setupTotp: (signal?: AbortSignal, setupToken?: string) =>
    api.post<TotpSetupResponse>('/Authentication/MFA/TOTP/Setup', undefined, {
      ...(signal !== undefined && { signal }),
      ...(setupToken && { headers: { Authorization: `Bearer ${setupToken}` } }),
    }).then(r => r.data),

  confirmTotp: async (code: string, totpSetupToken: string, loginSetupToken?: string): Promise<TotpConfirmResponse> => {
    try {
      const r = await api.post<TotpConfirmResponse>(
        '/Authentication/MFA/TOTP/Confirm',
        { Code: code, SetupToken: totpSetupToken },
        loginSetupToken ? { headers: { Authorization: `Bearer ${loginSetupToken}` } } : undefined,
      );
      return r.data;
    } catch (err: unknown) {
      const body = (err as { response?: { data?: TotpConfirmResponse } }).response?.data;
      if (body) return body;
      throw err;
    }
  },

  completeMfaSetup: (setupToken: string) =>
    api.post<LoginResponse>('/Authentication/MFA/Setup/Complete', { setupToken }).then(r => r.data),

  completeAfterTerms: (setupToken: string, options?: { allowSkipPendingTerms?: boolean }) =>
    api.post<MfaVerifyResponse>('/Authentication/CompleteAfterTerms', {
      setupToken,
      allowSkipPendingTerms: options?.allowSkipPendingTerms === true,
    }).then(r => r.data),

  beginPasskeyRegistration: (setupToken?: string) =>
    api.post<PasskeyRegistrationOptions>(
      '/Authentication/MFA/Passkey/BeginRegistration',
      undefined,
      setupToken ? { headers: { Authorization: `Bearer ${setupToken}` } } : undefined,
    ).then(r => r.data),

  completePasskeyRegistration: (req: PasskeyRegistrationRequest, setupToken?: string) =>
    api.post<PasskeyRegistrationResult>(
      '/Authentication/MFA/Passkey/CompleteRegistration',
      req,
      setupToken ? { headers: { Authorization: `Bearer ${setupToken}` } } : undefined,
    ).then(r => r.data),

  beginPasskeyAssertion: (mfaToken: string) =>
    api.post<PasskeyAssertionOptions>('/Authentication/MFA/Passkey/BeginAssertion', { MfaToken: mfaToken }).then(r => r.data),

  completePasskeyAssertion: (req: { MfaToken: string; AssertionJson: string }) =>
    api.post<MfaVerifyResponse>('/Authentication/MFA/Passkey/CompleteAssertion', req).then(r => r.data),

  deletePasskeyCredential: (credentialKey: number) =>
    api.delete<MfaMultiMethodSettings>(`/Authentication/MFA/Passkey/Credentials/${credentialKey}`).then(r => r.data),

  generateRecoveryCodes: () =>
    api.post<RecoveryCodesResponse>('/Authentication/MFA/RecoveryCodes/Generate').then(r => r.data),

  regenerateRecoveryCodes: () =>
    api.post<RecoveryCodesResponse>('/Authentication/MFA/RecoveryCodes/Regenerate').then(r => r.data),

  updatePassword: (req: UpdatePasswordRequest) =>
    api.post<UpdatePasswordResponse>('/Authentication/UpdatePassword', req).then(r => r.data),

  getPasswordInfo: () =>
    api.get<PasswordInfoResponse>('/Authentication/PasswordInfo').then(r => r.data),

  /** Trades the current restricted iCode order/reschedule session for a guest session (server
   *  mints a guest token scoped to the authenticated patient and swaps the BFF session). Lets
   *  the patient reach the appointments screen after booking. Returns the new guest session. */
  exitToGuestSession: () =>
    api.post<GuestSessionResponse>('/CreateAccount/GuestSessionToken', {}).then(r => r.data),
};
